import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Loader,
  Menu,
  Pagination,
  Table,
  TextInput
} from '@mantine/core';
import { Dots, Eye, Plus, Trash } from 'tabler-icons-react';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { useMediaQueries, useModalState } from '../../../helpers/hooks';
import HeaderView from '../../common/HeaderView';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { singularPluralFormat } from '../../../helpers/format';
import {
  PAYMENT_PLAN_TYPE_LIST,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import MenuContent from '../../common/MenuContent';
import ConfirmModal from '../../common/ConfirmModal';
import { triggerNotification } from '../../../helpers/notificationHelper';
import PaymentPlanChangeStatusModal from './PaymentPlanChangeStatusModal';

const PAYMENT_PLAN_TABLE_COLUMNS = [
  {
    label: 'Payment plan',
    value: 'name'
  },
  {
    label: 'Type',
    value: 'type'
  },
  {
    label: 'Status',
    value: 'status'
  },
  {
    label: 'Date created',
    value: 'created'
  }
];

const MOBILE_PLAN_TABLE_COLUMNS = [
  {
    label: 'Payment plan',
    value: 'name'
  }
];

const MAX_PAGE_SIZE = 10;

const PaymentPlanListView = () => {
  const { pkRegAssociation } = useParams();
  const hasFetched = useRef(false);
  const { isMobileOrSmaller } = useMediaQueries();
  const {
    state,
    fetchAdminRegAssociationPaymentPlans,
    deleteAdminRegAssociationPaymentPlans
  } = useContext(RegistrationAdminContext);
  const { state: dashboardState } = useContext(
    RegistrationAdminDashboardContext
  );
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [viewState, setViewState] = useState({
    search: '',
    pageIndex: 1,
    selectedPkRegAssociationPaymentPlans: []
  });

  const regAssociation = state.regAssociations.value.find(
    (a) =>
      a.pkRegAssociation ===
      dashboardState.regAssociation.value?.pkRegAssociation
  );

  const loadingPaymentPlans =
    !regAssociation ||
    !hasFetched.current ||
    state.regAssociationPaymentPlans.loading;
  const paymentPlans = state.regAssociationPaymentPlans.value
    .filter(
      (p) =>
        p.fkRegAssociation.toString() === pkRegAssociation &&
        (!viewState.search ||
          p.name.toLowerCase().includes(viewState.search.toLowerCase()))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const isAllSelected =
    !loadingPaymentPlans &&
    viewState.selectedPkRegAssociationPaymentPlans.length ===
      paymentPlans.length;
  const isSomeSelected =
    !loadingPaymentPlans &&
    viewState.selectedPkRegAssociationPaymentPlans.length > 0 &&
    !isAllSelected;

  useEffect(() => {
    if (regAssociation) {
      if (
        state.regAssociationPaymentPlans?.filter?.pkRegAssociation?.toString() !==
        pkRegAssociation.toString()
      ) {
        fetchAdminRegAssociationPaymentPlans(regAssociation.pkRegAssociation);
      }

      hasFetched.current = true;

      setViewState({
        search: '',
        pageIndex: 1,
        selectedPkRegAssociationPaymentPlans: []
      });
    }
  }, [regAssociation]);

  return (
    <HeaderView
      maxViewWidth={1100}
      style={{ height: '100%' }}
      viewProps={{ style: { height: '100%', overflow: 'hidden' } }}
    >
      <AppStack style={{ gap: 0, padding: 8, paddingTop: 20 }}>
        <AppFlexbox
          style={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <AppText style={{ fontSize: 20, fontWeight: 700 }}>
            Payment Plans
          </AppText>

          <Button
            color="dark"
            component={Link}
            leftSection={<Plus size={18} />}
            size="compact-sm"
            to={`/admin/settings/${pkRegAssociation}/payment-plans/create`}
            type="button"
            variant="filled"
          >
            Add Plan
          </Button>
        </AppFlexbox>
        <AppText style={{ fontSize: 14, color: '#666' }}>
          Set up payment plans for your association, allowing members to pay in
          installments at scheduled intervals.
        </AppText>
      </AppStack>
      <AppCard style={{ padding: 0, maxHeight: '100%' }} withBorder>
        <AppStack style={{ gap: 0, maxHeight: '100%', overflow: 'hidden' }}>
          <AppFlexbox
            style={{
              alignItems: 'center',
              padding: 8,
              justifyContent: 'space-between'
            }}
          >
            <TextInput
              onChange={(e) =>
                setViewState({
                  ...viewState,
                  search: e.currentTarget.value,
                  pageIndex: 1,
                  selectedPkRegAssociationPaymentPlans: []
                })
              }
              placeholder="Search by name..."
              style={{ flex: 1, maxWidth: isMobileOrSmaller ? '100%' : 300 }}
              value={viewState.search ?? ''}
            />
          </AppFlexbox>

          <Divider />
          <AppFlexbox
            style={{
              gap: 8,
              alignItems: 'center',
              padding: '8px 16px',
              justifyContent: 'space-between'
            }}
          >
            {loadingPaymentPlans ? (
              <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                <Loader color="dark" size={14} />
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  results
                </AppText>
              </AppFlexbox>
            ) : (
              <>
                {isSomeSelected ? (
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {viewState.selectedPkRegAssociationPaymentPlans.length} of{' '}
                    {paymentPlans.length} selected
                  </AppText>
                ) : (
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {singularPluralFormat(
                      paymentPlans.length,
                      'result',
                      'results'
                    )}
                  </AppText>
                )}

                {viewState.selectedPkRegAssociationPaymentPlans.length > 0 && (
                  <MenuContent
                    control={
                      <ActionIcon color="#ced4da" size="md" variant="outline">
                        <Dots color="#000" />
                      </ActionIcon>
                    }
                    styles={{ dropdown: { padding: 0 } }}
                  >
                    <AppStack style={{ padding: 2, gap: 0 }}>
                      <Menu.Item
                        leftSection={<Eye size={18} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onOpenModal(VIEW_ACTIONS_ENUM.CHANGE_STATUS, [
                            ...paymentPlans.filter((s) =>
                              viewState.selectedPkRegAssociationPaymentPlans.includes(
                                s.pkRegAssociationPaymentPlan
                              )
                            )
                          ]);
                        }}
                      >
                        Change status
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<Trash size={18} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onOpenModal(VIEW_ACTIONS_ENUM.DELETE, [
                            ...paymentPlans.filter((s) =>
                              viewState.selectedPkRegAssociationPaymentPlans.includes(
                                s.pkRegAssociationPaymentPlan
                              )
                            )
                          ]);
                        }}
                        style={{ color: 'red' }}
                      >
                        Delete plans
                      </Menu.Item>
                    </AppStack>
                  </MenuContent>
                )}
              </>
            )}
          </AppFlexbox>

          <AppStack style={{ gap: 0, height: '100%', overflow: 'auto' }}>
            <Table style={{ height: '100%' }}>
              <Table.Thead>
                <Table.Tr>
                  {(isMobileOrSmaller
                    ? MOBILE_PLAN_TABLE_COLUMNS
                    : PAYMENT_PLAN_TABLE_COLUMNS
                  ).map((c, index) => (
                    <Table.Th
                      key={c.value}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'rgba(56, 56, 56, 0.1)'
                      }}
                    >
                      <AppFlexbox style={{ gap: 16, alignItems: 'center' }}>
                        {index === 0 && (
                          <Checkbox
                            checked={isAllSelected || false}
                            disabled={
                              loadingPaymentPlans || paymentPlans.length === 0
                            }
                            indeterminate={isSomeSelected}
                            onChange={() => {
                              if (isAllSelected) {
                                setViewState({
                                  ...viewState,
                                  selectedPkRegAssociationPaymentPlans: []
                                });
                              }
                              else {
                                setViewState({
                                  ...viewState,
                                  selectedPkRegAssociationPaymentPlans: paymentPlans.map(
                                    (p) => p.pkRegAssociationPaymentPlan
                                  )
                                });
                              }
                            }}
                          />
                        )}

                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          {c.label}
                        </AppText>
                      </AppFlexbox>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loadingPaymentPlans ? (
                  <Table.Tr>
                    <Table.Td
                      colSpan={PAYMENT_PLAN_TABLE_COLUMNS.length}
                      style={{ padding: 40 }}
                    >
                      <AppStack style={{ gap: 8, alignItems: 'center' }}>
                        <Loader color="dark" size={40} />
                      </AppStack>
                    </Table.Td>
                  </Table.Tr>
                ) : paymentPlans.length === 0 ? (
                  <Table.Tr>
                    <Table.Td
                      colSpan={PAYMENT_PLAN_TABLE_COLUMNS.length}
                      style={{ padding: 40 }}
                    >
                      <AppStack style={{ gap: 16, alignItems: 'center' }}>
                        <AppText
                          style={{
                            fontSize: 14,
                            textAlign: 'center',
                            color: '#666',
                            fontStyle: 'italic'
                          }}
                        >
                          {!viewState.search
                            ? `No payment plans have been set up yet.`
                            : `No payment plans match your search.`}
                        </AppText>

                        <Button
                          color="dark"
                          component={Link}
                          leftSection={<Plus size={18} />}
                          size="compact-sm"
                          to={`/admin/settings/${pkRegAssociation}/payment-plans/create`}
                          type="button"
                          variant="filled"
                        >
                          Add Plan
                        </Button>
                      </AppStack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  paymentPlans
                    .slice(
                      -MAX_PAGE_SIZE + viewState.pageIndex * MAX_PAGE_SIZE,
                      viewState.pageIndex * MAX_PAGE_SIZE
                    )
                    .map((plan) => (
                      <Table.Tr key={plan.pkRegAssociationPaymentPlan}>
                        <Table.Td style={{ padding: '8px 16px' }}>
                          <AppFlexbox
                            component={isMobileOrSmaller ? Link : null}
                            style={{
                              gap: 16,
                              alignItems: 'center',
                              textDecoration: 'none',
                              color: '#000'
                            }}
                            to={`/admin/settings/${pkRegAssociation}/payment-plans/${plan.pkRegAssociationPaymentPlan}`}
                          >
                            <Checkbox
                              checked={viewState.selectedPkRegAssociationPaymentPlans.includes(
                                plan.pkRegAssociationPaymentPlan
                              )}
                              onChange={() => {
                                if (
                                  viewState.selectedPkRegAssociationPaymentPlans.includes(
                                    plan.pkRegAssociationPaymentPlan
                                  )
                                ) {
                                  setViewState({
                                    ...viewState,
                                    selectedPkRegAssociationPaymentPlans: viewState.selectedPkRegAssociationPaymentPlans.filter(
                                      (pk) =>
                                        pk !== plan.pkRegAssociationPaymentPlan
                                    )
                                  });
                                }
                                else {
                                  setViewState({
                                    ...viewState,
                                    selectedPkRegAssociationPaymentPlans: [
                                      ...viewState.selectedPkRegAssociationPaymentPlans,
                                      plan.pkRegAssociationPaymentPlan
                                    ]
                                  });
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                            <AppStack style={{ gap: 0 }}>
                              <Anchor
                                component={Link}
                                style={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: '#000'
                                }}
                                to={`/admin/settings/${pkRegAssociation}/payment-plans/${plan.pkRegAssociationPaymentPlan}`}
                              >
                                {plan.name}
                              </Anchor>
                              <Anchor
                                component={Link}
                                style={{
                                  fontSize: 14,
                                  color: '#666'
                                }}
                                to={`/admin/settings/${pkRegAssociation}/payment-plans/${plan.pkRegAssociationPaymentPlan}`}
                              >
                                {singularPluralFormat(
                                  plan.regAssociationPaymentPlanInstallments
                                    .length,
                                  'installment',
                                  'installments'
                                )}
                              </Anchor>

                              {isMobileOrSmaller && (
                                <AppText
                                  style={{
                                    fontSize: 14,
                                    color: plan.active ? 'green' : '#C40000'
                                  }}
                                >
                                  {plan.active ? 'Active' : 'Disabled'}
                                </AppText>
                              )}
                            </AppStack>
                          </AppFlexbox>
                        </Table.Td>

                        {!isMobileOrSmaller && (
                          <>
                            <Table.Td style={{ padding: '8px 16px' }}>
                              <AppStack style={{ gap: 0 }}>
                                <AppText style={{ fontSize: 14 }}>
                                  {PAYMENT_PLAN_TYPE_LIST.find(
                                    (t) =>
                                      t.value ===
                                      plan.fkRegAssociationPaymentPlanType.toString()
                                  )?.label || 'Unknown Type'}
                                </AppText>
                              </AppStack>
                            </Table.Td>

                            <Table.Td style={{ padding: '8px 16px' }}>
                              <AppStack style={{ gap: 0 }}>
                                <AppText
                                  style={{
                                    fontSize: 14,
                                    color: plan.active ? 'green' : '#C40000'
                                  }}
                                >
                                  {plan.active ? 'Active' : 'Disabled'}
                                </AppText>
                              </AppStack>
                            </Table.Td>

                            <Table.Td style={{ padding: '8px 16px' }}>
                              <AppStack style={{ gap: 0 }}>
                                <AppText style={{ fontSize: 14 }}>
                                  {dayjs(plan.createdAt).format('MMM D, YYYY')}
                                </AppText>
                              </AppStack>
                            </Table.Td>
                          </>
                        )}
                      </Table.Tr>
                    ))
                )}
              </Table.Tbody>
            </Table>
          </AppStack>
        </AppStack>
      </AppCard>
      {paymentPlans.length > MAX_PAGE_SIZE && (
        <AppFlexbox style={{ justifyContent: 'center', paddingBottom: 20 }}>
          <Pagination
            color="blue"
            onChange={(page) => {
              setViewState({
                ...viewState,
                pageIndex: page
              });
            }}
            total={Math.ceil(paymentPlans.length / MAX_PAGE_SIZE)}
            value={viewState.pageIndex}
          />
        </AppFlexbox>
      )}

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText={`Yes, delete ${
          modalState.item?.length > 1 ? 'plans' : 'plan'
        }`}
        isLoading={modalState.loading}
        isNewModalStyle
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText>
              Are you sure you want to delete{' '}
              <b>
                {modalState.item?.length === 1
                  ? modalState.item[0].name
                  : `${modalState.item?.length} plans`}
              </b>
              ?
            </AppText>

            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action can’t be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteAdminRegAssociationPaymentPlans(
            regAssociation.pkRegAssociation,
            modalState.item?.map((p) => p.pkRegAssociationPaymentPlan),
            () => {
              triggerNotification(
                `${singularPluralFormat(
                  modalState.item.length,
                  'plan',
                  'plans'
                )} deleted!`,
                'success'
              );
              onChangeModalLoading(false);
              setViewState({
                ...viewState,
                selectedPkRegAssociationPaymentPlans: []
              });
              onCloseModal();
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${
          modalState.item?.length === 1
            ? modalState.item[0].name
            : `${modalState.item?.length} plans`
        }?`}
        topPadding={0}
      />
      <PaymentPlanChangeStatusModal
        isOpen={
          modalState.isOpen &&
          modalState.action === VIEW_ACTIONS_ENUM.CHANGE_STATUS
        }
        onClose={onCloseModal}
        pkRegAssociation={regAssociation?.pkRegAssociation}
        pkRegAssociationPaymentPlans={
          modalState.action === VIEW_ACTIONS_ENUM.CHANGE_STATUS
            ? modalState.item
            : []
        }
      />
    </HeaderView>
  );
};

export default PaymentPlanListView;
